import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isFunction, size } from 'underscore';
import classNames from 'classnames';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useClientContext } from '@bingads-webui-campaign-react/smart-page-context';
import { List } from '@fluentui/react/lib/List';
import { withErrorView } from '@bingads-webui-campaign-react/smart-page-error-boundary';
import { ImageItemView } from '@bingads-webui-campaign-react/smart-component-image';

const getStyles = components => ({
  container: {
    ...components.section,
    marginLeft: '10%',
    marginRight: '10%',
  },
  title: {
    ...components.sectionTitle,
  },
  list: {
    overflow: 'auto',
  },
  centeredList: {
    '& .ms-List-page': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  listCellContainer: {
    textAlign: 'center',
    outline: 'none',
    position: 'relative',
    float: 'left',
  },
  imageContainer: {
    position: 'absolute',
    left: 16,
    top: 16,
    right: 16,
    bottom: 16,
  },
});

const ROWS_PER_PAGE = 1;
const MAX_ITEM_WIDTH = 320;

export const UnStyledSmartGalleryView = ({
  items,
  i18n,
  id,
}) => {
  const classes = useDefaultStyles(getStyles);
  const columnCount = useRef(0);
  const rowHeight = useRef(0);

  const componentRef = useRef();
  const [previousResponsiveMode, setPreviousResponsiveMode] = useState(null);
  const { responsiveMode } = useClientContext();

  useEffect(() => {
    // Responsive mode change somehow not able to trigger
    // list rerender in editor. explicitly trigger it.
    if (responsiveMode !== previousResponsiveMode) {
      if (componentRef
        && componentRef.current
        && isFunction(componentRef.current.forceUpdate)) {
        componentRef.current.forceUpdate();
      }

      setPreviousResponsiveMode(responsiveMode);
    }
  }, [
    previousResponsiveMode,
    setPreviousResponsiveMode,
    responsiveMode,
    componentRef,
  ]);

  const onRenderCell = useCallback(item => (
    <div
      className={classes.listCellContainer}
      style={{
        width: rowHeight.current,
        height: rowHeight.current,
      }}
    >
      <div className={classes.imageContainer}>
        <ImageItemView
          imageId={item.imageId}
          resizeWidth={MAX_ITEM_WIDTH}
          src={item.fbImageUrl} /* backup src */
          retryLimit={3}
        />
      </div>
    </div>
  ), [classes.listCellContainer, classes.imageContainer]);

  const getItemCountForPage = React.useCallback((itemIndex, surfaceRect) => {
    if (itemIndex === 0) {
      columnCount.current = Math.ceil(surfaceRect.width / MAX_ITEM_WIDTH);
      // if using square
      rowHeight.current = Math.floor(surfaceRect.width / columnCount.current);
    }
    return columnCount.current * ROWS_PER_PAGE;
  }, []);
  const getPageHeight = useCallback(() => rowHeight.current * ROWS_PER_PAGE, []);

  const listClassName = size(items) > columnCount.current ? classes.list : classes.centeredList;

  return (
    <div id={id} className={classes.container}>
      <h2 className={classNames(classes.title, 'component-title')}>
        {i18n.getString(_TL_('Gallery'))}
      </h2>
      {
        !isEmpty(items) &&
        <List
          items={items}
          className={listClassName}
          getPageHeight={getPageHeight}
          getItemCountForPage={getItemCountForPage}
          onRenderCell={onRenderCell}
          componentRef={componentRef}
        />
      }
    </div>
  );
};

UnStyledSmartGalleryView.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    imageId: PropTypes.string.isRequired,
  })),
};

UnStyledSmartGalleryView.defaultProps = {
  items: [],
};

export const SmartGalleryView = withErrorView(UnStyledSmartGalleryView, 'componentMode');
